import { useState, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import internetqLogo from '@/assets/internetq-logo.png';

/**
 * MFA-Code-Eingabe-Seite (Spec 0045 Phase 4).
 * Wird nach Login angezeigt, wenn Cognito SOFTWARE_TOKEN_MFA Challenge sendet.
 * Oeffentliche Route (User ist noch nicht fertig authentifiziert).
 */
export function MfaVerifyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { confirmMfaCode, signOut } = useAuth();

  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (code.length !== 6) {
      setError(t('mfaVerify.error.codeRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await confirmMfaCode(code);

      if (result.success) {
        const redirectTarget = sessionStorage.getItem('redirectAfterLogin') || '/';
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectTarget, { replace: true });
        return;
      }

      // Code ungueltig
      setError(t('mfaVerify.error.invalidCode'));
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAuthorizedException') {
        setError(t('mfaVerify.error.invalidCode'));
      } else {
        setError(t('mfaVerify.error.verifyFailed'));
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Nur Ziffern erlauben, Auto-Submit bei 6 Stellen
  const handleCodeChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setCode(cleaned);
  };

  const handleCancel = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-6">
          {/* Brand */}
          <div className="mb-6 flex flex-col items-center gap-2">
            <img
              src={internetqLogo}
              alt="InternetQ"
              className="h-10 w-auto dark:brightness-0 dark:invert"
            />
            <span className="text-xs font-medium text-muted-foreground">Admin Panel</span>
          </div>

          <h2 className="mb-4 text-center text-lg font-semibold">
            {t('mfaVerify.title')}
          </h2>

          <p className="mb-4 text-center text-sm text-muted-foreground">
            {t('mfaVerify.intro')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="mfa-code">{t('mfaVerify.codeLabel')}</Label>
              <Input
                ref={inputRef}
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                disabled={isSubmitting}
                className="text-center text-2xl tracking-[0.5em] font-mono"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || code.length !== 6}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  {t('mfaVerify.submitting')}
                </span>
              ) : (
                t('mfaVerify.submit')
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleCancel}
                className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
